/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// pcg-report core: HCP/AI governance, feed concordance + audit log, panel state,
// questionnaire flags.
// Source: cardiac_report_v0_5_6_dev_2.html lines 4722-4791 (governance),
// 4956-5058 (feed concordance suite), 5140-5156 (buildFeedAgreementLog),
// 4793-4801 (getPanelState), 5803-5820 (getQuestionnaireFlags), 5693-5703
// (getAnswerClass), 5120-5131 (contextSummaryForFeature).
// Deviations: data globals -> explicit arguments; capturedAt Date -> `now` option.

import { getDetailedZone, getColorClass } from "./status"
import { calculateMarkerPosition } from "./marker"
import { sourceIdForFeed, reportFeatures } from "./features"

export function aiStatusToDetailedZone(aiStatus, scaleType) {
  if (scaleType === "unidirectional") {
    if (aiStatus === "normal") return "green"
    if (aiStatus === "borderline") return "amber"
    return "red" // elevated, low
  }
  // Bidirectional — AI dropdown has specific low/high options
  switch (aiStatus) {
    case "normal":
      return "green"
    case "borderline-low":
      return "amber-low"
    case "borderline-high":
      return "amber-high"
    case "borderline":
      return "amber-high" // legacy fallback — treat as high
    case "low":
      return "red-low"
    case "elevated":
      return "red-high"
    default:
      return "green"
  }
}

export function getGovernanceCase(feature, hrBpm) {
  // HCP zone derived from marker position (fine-grained)
  const markerPos =
    feature.markerPosition !== undefined ? feature.markerPosition : calculateMarkerPosition(feature, hrBpm)
  const hcpZone = getDetailedZone(markerPos, feature.scaleType)

  // AI zone derived from AI status (fine-grained)
  const aiRaw = feature.aiStatus || "normal"
  if (aiRaw === "not-diagnosed") {
    return { showNumerics: false, caseLabel: "HCP assessed / AI N/A" }
  }
  const aiZone = aiStatusToDetailedZone(aiRaw, feature.scaleType)

  // Compare detailed zones — must match exactly
  if (hcpZone === aiZone) {
    return { showNumerics: true, caseLabel: "HCP + AI agree (" + hcpZone + ") — numerics shown" }
  } else {
    return { showNumerics: false, caseLabel: "HCP (" + hcpZone + ") / AI (" + aiZone + ") disagree — numerics hidden" }
  }
}

export function recomputeDataSource(feature, hrBpm) {
  const gov = getGovernanceCase(feature, hrBpm)
  feature.dataSource = gov.showNumerics ? "both" : "hcp"
  return feature.dataSource
}

export function sourceResultKey(source) {
  const text = `${source.name || ""} ${source.finding || ""}`.toLowerCase()
  if (text.includes("some repeated") || text.includes("repeated sound pattern supported")) return "amber"
  if (
    text.includes("murmur detection") &&
    (text.includes("supported") || text.includes("positive") || text.includes("present"))
  )
    return "amber"
  if (text.includes("elevated") || text.includes("abnormal") || text.includes("prominent") || text.includes("outside"))
    return "red"
  if (text.includes("borderline") || text.includes("variable") || text.includes("fair") || text.includes("limited"))
    return "amber"
  if (
    text.includes("normal") ||
    text.includes("consistent") ||
    text.includes("within") ||
    text.includes("regular cycles") ||
    text.includes("average heart rate") ||
    text.includes("median beat interval") ||
    text.includes("mean systolic/diastolic ratio") ||
    text.includes("cardiac cycle timing") ||
    text.includes("regularity is high") ||
    text.includes("reliable")
  )
    return "green"
  return "unknown"
}

export function reviewerResultKey(feature, hrBpm) {
  const status =
    feature.status || getColorClass(feature.name, feature.observed, feature.normalRange, null, feature.scaleType, hrBpm)
  if (status === "red") return "red"
  if (status === "amber") return "amber"
  if (status === "green") return "green"
  return "unknown"
}

// The PCG Viewer's measurements define every card's draft; a provider's
// evidence (ACT, BeatAware) is shown beside it with the decision recorded in
// the viewer, and never assigns a provider a clinical role of its own.
const PROVIDER_EVIDENCE_RULE =
  "PCG Viewer measurements define the draft. Applicable provider evidence is shown separately with the disposition recorded in the Viewer."

export function getCardRule(feature) {
  const type = String((feature && (feature.type || feature.name)) || "").toLowerCase()
  if (type.includes("timing") || type.includes("bpm") || type.includes("rhythm") || type.includes("ratio")) {
    return {
      ruleId: "timing_summary_exam_rollup",
      ruleVersion: "0.1",
      primaryFeed: "pcg_viewer",
      summary: PROVIDER_EVIDENCE_RULE,
    }
  }
  if (type.includes("signal")) {
    return {
      ruleId: "recording_reliability_gate",
      ruleVersion: "0.1",
      primaryFeed: "pcg_viewer",
      summary: "PCG Viewer/acquisition reliability primary; other feeds are advisory.",
    }
  }
  return {
    ruleId: "default_card_rule",
    ruleVersion: "0.1",
    primaryFeed: "pcg_viewer",
    summary: PROVIDER_EVIDENCE_RULE,
  }
}

// How a feed reads on the card: the PCG Viewer is the Medaica result, every
// other feed is provider evidence.
export function feedRole(feature, source) {
  return sourceIdForFeed(source) === "pcg_viewer" ? "Medaica result" : "provider evidence"
}

export function feedAgreement(feature, source, hrBpm) {
  if (!source) return "neutral"
  if (source.reviewStatus === "rejected") return "excluded"
  const sourceKey = sourceResultKey(source)
  const reviewerKey = reviewerResultKey(feature, hrBpm)
  if (sourceKey === "unknown" || reviewerKey === "unknown") return "neutral"
  return sourceKey === reviewerKey ? "concordant" : "discordant"
}

export function feedState(feature, source) {
  if (!source) return "unavailable"
  if (source.reviewStatus === "rejected") return "excluded_from_evidence"
  return "fed_draft"
}

export function sourceConcordanceLabel(feature, source, hrBpm) {
  if (!feature.approved) {
    if (source.reviewStatus === "rejected") return "Excluded"
    return "Fed draft"
  }
  const agreement = feedAgreement(feature, source, hrBpm)
  if (agreement === "concordant") return "Matched card"
  if (agreement === "discordant") return "Differed"
  if (agreement === "excluded") return "Excluded"
  return "Neutral"
}

export function hasReviewedSource(feature) {
  return (feature.sources || []).some((s) => s.reviewStatus === "accepted" || s.reviewStatus === "rejected")
}

// Captured at approval time; supports the regulatory audit trail.
export function buildFeedAgreementLog(feature, { now = () => new Date().toISOString(), hrBpm = null } = {}) {
  const rule = getCardRule(feature)
  const hcpAcceptedOutcome = reviewerResultKey(feature, hrBpm)
  return (feature.sources || []).map((source) => ({
    sourceId: sourceIdForFeed(source),
    sourceLabel: source.name || "Unknown feed",
    feedRole: feedRole(feature, source),
    feedState: feedState(feature, source),
    feedOutcome: sourceResultKey(source),
    hcpAcceptedOutcome,
    agreement: feedAgreement(feature, source, hrBpm),
    excludedReason: source.rejectReasonCode || source.rejectReasonText || null,
    cardRuleId: rule.ruleId,
    cardRuleVersion: rule.ruleVersion,
    capturedAt: now(),
  }))
}

// Panel lifecycle: preliminary -> in_review -> approved (-> released: never set by the prototype).
export function getPanelState(features, { adminMode = false, viewMode = "provider" } = {}) {
  const visibleFeatures = reportFeatures(features, { adminMode, viewMode })
  const allApproved = visibleFeatures.length > 0 && visibleFeatures.every((f) => f.approved)
  const anyApproved = visibleFeatures.some((f) => f.approved)
  if (allApproved) return "approved"
  if (adminMode || anyApproved) return "in_review"
  return "preliminary"
}

export function getQuestionnaireFlags(q) {
  const flags = []
  if (!q || !q.sections) return flags
  q.sections.forEach((s) => {
    s.items.forEach((item) => {
      if (!item.a) return
      const a = item.a.toLowerCase()
      if (a === "yes" || a === "not sure") {
        flags.push({
          text: s.title,
          answer: item.a,
          level: a === "yes" ? "red" : "amber",
        })
      }
    })
  })
  return flags
}

export function getAnswerClass(answer) {
  if (!answer) return "q-na"
  const a = answer.toLowerCase()
  if (a === "no" || a === "no change") return "q-no"
  if (a === "yes") return "q-yes"
  if (a === "not sure" || a === "not answered") return "q-concern"
  // Specific clinical concern answers
  const concernAnswers = [
    "much worse",
    "both",
    "longer than 10 minutes",
    "more than once",
    "within last 3 months",
    "worsens during day",
  ]
  if (concernAnswers.includes(a)) return "q-yes"
  return "q-neutral"
}

export function contextSummaryForFeature(feature, questionnaire) {
  if (!feature) return ""
  const flags = getQuestionnaireFlags(questionnaire)
  if (!flags.length) return ""
  const structuralCards = [
    "Structural Sound",
    "Structural Sound Summary",
    "Structural Sound Detail",
    "Structural Sound Finding",
    "Murmur Finding",
    "S1/S2 Intensity",
  ]
  const rhythmCards = ["Cycle Regularity", "Rhythm Regularity", "Heart Rate", "Timing Summary"]
  if (structuralCards.includes(feature.name))
    return `context: ${flags
      .slice(0, 2)
      .map((f) => f.text)
      .join(", ")}`
  if (
    rhythmCards.includes(feature.name) &&
    flags.some((f) => ["Dizziness or fainting", "Shortness of breath", "Fatigue or reduced activity"].includes(f.text))
  ) {
    return "context relevant"
  }
  return ""
}
